import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { type MessageTemplate, type MessageTemplateCategory } from '@/constants/messageTemplates';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import { getMessageTemplatesV2QueryOptions } from '@/services/getMessageTemplatesV2';
import { userIdAtom } from '@/services/globalState';
import { supportMessageAudienceSchema } from '@/types/messages/supportMessage';

type MessageTemplateConfig = {
  isEnabled: boolean;
  categories: Record<MessageTemplateCategory, string>;
  templates: MessageTemplate[];
};

type ParsedMessageTemplateConfig = Omit<MessageTemplateConfig, 'isEnabled'>;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function formatTemplateCategoryLabel(category: string): string {
  return category
    .split(/[_-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function getCategoryLabel(
  category: MessageTemplateCategory,
  categories: Record<MessageTemplateCategory, string>
): string {
  return categories[category] ?? formatTemplateCategoryLabel(category);
}

function isValidTemplateCandidate(candidate: Record<string, unknown>): candidate is {
  id: string;
  title: string;
  body: string;
  category: string;
  audience?: unknown;
} {
  return (
    isNonEmptyString(candidate.id) &&
    isNonEmptyString(candidate.title) &&
    isNonEmptyString(candidate.body) &&
    isNonEmptyString(candidate.category)
  );
}

function parseTemplateConfig(value: unknown): ParsedMessageTemplateConfig | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }

  const parsed = value as Record<string, unknown>;

  const rawCategories = parsed.categories;

  const rawTemplates = parsed.templates;

  if (!Array.isArray(rawTemplates)) {
    return null;
  }

  const categories: Record<MessageTemplateCategory, string> = {};

  if (
    typeof rawCategories === 'object' &&
    rawCategories !== null &&
    !Array.isArray(rawCategories)
  ) {
    for (const [category, label] of Object.entries(rawCategories)) {
      if (isNonEmptyString(category) && isNonEmptyString(label)) {
        categories[category.trim()] = label.trim();
      }
    }
  }

  const templates = rawTemplates.flatMap((template): MessageTemplate[] => {
    if (typeof template !== 'object' || template === null || Array.isArray(template)) {
      return [];
    }

    const candidate = template as Record<string, unknown>;

    if (!isValidTemplateCandidate(candidate)) {
      return [];
    }

    const category = candidate.category.trim();

    if (!categories[category]) {
      categories[category] = formatTemplateCategoryLabel(category);
    }

    return [
      {
        id: candidate.id.trim(),
        title: candidate.title.trim(),
        body: candidate.body.trim(),
        category,
        // Payloads written before audiences existed are all submitter-facing.
        audience: supportMessageAudienceSchema.parse(candidate.audience) ?? 'submitter',
      },
    ];
  });

  if (templates.length === 0) {
    return null;
  }

  return {
    categories,
    templates,
  };
}

/**
 * The canned support messages for the composer's Template action.
 *
 * Templates come only from the API. The endpoint 403s for anyone who cannot send
 * support messages, so it is only called for Primum staff. `isEnabled` is true
 * once the API has answered with at least one valid template, so a failed
 * request, a 403, or every template being switched off in Admin hides the
 * action instead of showing an error.
 */
export function useMessageTemplates(): MessageTemplateConfig {
  const { isPrimumUser } = useCognitoGroups();

  const userId = useAtomValue(userIdAtom);

  const { data: apiValue } = useQuery(getMessageTemplatesV2QueryOptions(userId, isPrimumUser));

  return useMemo(() => {
    const parsed = isPrimumUser ? parseTemplateConfig(apiValue) : null;

    if (!parsed) {
      return { isEnabled: false, categories: {}, templates: [] };
    }

    parsed.templates.forEach((template) => {
      parsed.categories[template.category] = getCategoryLabel(template.category, parsed.categories);
    });

    return {
      isEnabled: true,
      categories: parsed.categories,
      templates: parsed.templates,
    };
  }, [apiValue, isPrimumUser]);
}
